import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Printer, Trash2, Search } from "lucide-react";
import { buscarProveedorPorClave } from "../services/proveedoresService";
import { buscarArticuloPorClave } from "../services/articuloService";
import {
  obtenerOrdenCompra,
  crearOrdenCompra,
  actualizarOrdenCompra,
  obtenerSiguienteFolioOC,
} from "../services/ordenesCompraService";
import PageHeader from "../components/PageHeader";
import Toast, { useToast } from "../components/Toast";

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num    = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt    = (n) =>
  `$${round2(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = () => new Date().toISOString().slice(0, 10);
const apiDate = (d) => (d ? d.replace(/-/g, "/") : "");
const htmlDate = (d) => (d ? d.replace(/\//g, "-") : "");

const EMPTY_ROW = () => ({
  articulo: "", cantidad: 1, unidadMedida: "", descripcion: "",
  precioUnitario: 0, porcDescuento: 0, importe: 0,
});

const EMPTY_PROV = {
  clave: "", nombre: "", rfc: "", direccion: "", colonia: "",
  ciudad: "", estado: "", cp: "", telefono: "",
};

/* ── CellInput — definido fuera para no perder foco ─────────────────────── */
const CellInput = ({ value, onChange, onBlur, type = "text", align = "left", readOnly = false }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={onChange}
    onBlur={onBlur}
    readOnly={readOnly}
    step={type === "number" ? "any" : undefined}
    style={{
      width: "100%", padding: "5px 7px", border: "1px solid var(--clr-border)",
      borderRadius: "var(--radius-sm)", fontSize: "var(--fs-sm)", background: readOnly ? "#f9fafb" : "#fff",
      outline: "none", textAlign: align, boxSizing: "border-box",
    }}
  />
);

/* ── Página ──────────────────────────────────────────────────────────────── */
export default function OrdenesCompraFormPage() {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const isEdit     = Boolean(id);
  const empresaId  = Number(localStorage.getItem("empresaId") || 1);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const [folio,   setFolio]   = useState("");
  const [prov,    setProv]    = useState({ ...EMPTY_PROV });
  const [provMsg, setProvMsg] = useState("");
  const [form,    setForm]    = useState({
    fecha: today(), moneda: "P", porcDcto: 0, surtida: false,
    notasAdicionales: "", observaciones: "",
  });
  const [rows,    setRows]    = useState([EMPTY_ROW()]);
  const [rowMsgs, setRowMsgs] = useState({});
  const [saving,  setSaving]  = useState(false);

  /* ── Cargar folio siguiente o datos para edición ─────────────────────── */
  useEffect(() => {
    if (isEdit) {
      obtenerOrdenCompra(Number(id), empresaId)
        .then((data) => {
          setFolio(data.ordenCompra);
          setProv({
            clave: data.proveedor, nombre: data.nombreProv, rfc: data.rfcProv,
            direccion: "", colonia: "", ciudad: "", estado: "", cp: "", telefono: "",
          });
          setForm({
            fecha: htmlDate(data.fecha),
            moneda: data.moneda,
            porcDcto: data.porcDcto,
            surtida: data.surtida,
            notasAdicionales: data.notasAdicionales || "",
            observaciones: data.observaciones || "",
          });
          setRows(
            data.detalle.length
              ? data.detalle.map((d) => ({
                  articulo: d.articulo, cantidad: d.cantidad,
                  unidadMedida: d.unidadMedida, descripcion: d.descripcion,
                  precioUnitario: d.precioUnitario, porcDescuento: d.porcDescuento,
                  importe: d.importe,
                }))
              : [EMPTY_ROW()]
          );
        })
        .catch(() => toastError("Error al cargar la orden de compra."));
    } else {
      obtenerSiguienteFolioOC(empresaId)
        .then(setFolio)
        .catch(() => setFolio("—"));
    }
  }, [id]); // eslint-disable-line

  /* ── Lookup de proveedor ────────────────────────────────────────────────── */
  const lookupProveedor = useCallback(async () => {
    if (!prov.clave.trim()) return;
    try {
      const p = await buscarProveedorPorClave(prov.clave.trim().toUpperCase());
      if (p) {
        setProv({
          clave: p.clave, nombre: p.nombre, rfc: p.rfc,
          direccion: p.direccion, colonia: p.colonia,
          ciudad: p.ciudad, estado: p.estado, cp: p.cp, telefono: p.telefono,
        });
        setProvMsg("");
      } else {
        setProvMsg("Proveedor no encontrado.");
      }
    } catch {
      setProvMsg("Error al buscar proveedor.");
    }
  }, [prov.clave]);

  /* ── Lookup de artículo ─────────────────────────────────────────────────── */
  const lookupArticulo = useCallback(async (idx) => {
    const clave = rows[idx]?.articulo?.trim();
    if (!clave) return;
    try {
      const a = await buscarArticuloPorClave(empresaId, clave.toUpperCase());
      if (a) {
        const precio = Number(
          a.precioUnitario ?? a.PrecioUnitario ?? a.precio_Unitario ??
          a.Precio ?? a.precio ?? 0
        ) || 0;
        setRows((prev) =>
          prev.map((r, i) =>
            i !== idx ? r : {
              ...r,
              articulo:       a.clave || a.Articulo || clave,
              descripcion:    a.descripcion  || a.Descripcion  || "",
              unidadMedida:   a.unidadMedida || a.UnidadMedida || "",
              precioUnitario: precio,
              importe:        round2(num(r.cantidad) * precio),
            }
          )
        );
        setRowMsgs((m) => ({ ...m, [idx]: "" }));
      } else {
        setRowMsgs((m) => ({ ...m, [idx]: "Artículo no encontrado." }));
      }
    } catch {
      setRowMsgs((m) => ({ ...m, [idx]: "Error al buscar." }));
    }
  }, [rows, empresaId]);

  /* ── Cambios en filas ──────────────────────────────────────────────────── */
  const onRowChange = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: value };
        if (field === "cantidad" || field === "precioUnitario") {
          updated.importe = round2(num(updated.cantidad) * num(updated.precioUnitario));
        }
        return updated;
      })
    );
  };

  const addRow  = () => setRows((r) => [...r, EMPTY_ROW()]);
  const rmRow   = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

  /* ── Totales ─────────────────────────────────────────────────────────────── */
  const totales = useMemo(() => {
    let subtAntes = 0, descTotal = 0;
    for (const r of rows) {
      if (!r.articulo) continue;
      subtAntes  += num(r.importe);
      descTotal  += num(r.importe) * (num(r.porcDescuento) / 100);
    }
    const sub = round2(subtAntes - descTotal);
    const iva = round2(sub * 0.16);
    return { subtAntes: round2(subtAntes), desc: round2(descTotal), sub, iva, tot: round2(sub + iva) };
  }, [rows]);

  /* ── Guardar ─────────────────────────────────────────────────────────────── */
  const handleGuardar = async () => {
    if (!prov.clave) return toastError("Selecciona un proveedor.");
    const lineas = rows.filter((r) => r.articulo.trim());
    if (!lineas.length) return toastError("Agrega al menos un artículo.");

    setSaving(true);
    try {
      const payload = {
        proveedor: prov.clave.toUpperCase(),
        fecha: apiDate(form.fecha),
        moneda: form.moneda,
        porcDcto: num(form.porcDcto),
        notasAdicionales: form.notasAdicionales,
        observaciones: form.observaciones,
        surtida: form.surtida,
        empresa: empresaId,
        detalle: lineas.map((r, i) => ({
          renglon:        i + 1,
          articulo:       r.articulo.toUpperCase(),
          cantidad:       num(r.cantidad),
          unidadMedida:   r.unidadMedida,
          descripcion:    r.descripcion,
          precioUnitario: num(r.precioUnitario),
          porcDescuento:  num(r.porcDescuento),
          importe:        num(r.importe),
        })),
      };

      if (isEdit) {
        await actualizarOrdenCompra(Number(id), payload);
        toastSuccess("Orden actualizada correctamente.");
      } else {
        const res = await crearOrdenCompra(payload);
        toastSuccess(res.message || "Orden creada.");
      }
      setTimeout(() => navigate("/proveedores/ordenes-compra"), 800);
    } catch (e) {
      toastError(e?.response?.data?.message || "Error al guardar la orden.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader
        title={isEdit ? `Editar OC-${id}` : "Nueva Orden de Compra"}
        subtitle="Orden de compra a proveedor"
      />

      {/* ── Encabezado ── */}
      <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Encabezado</h3>
          <span style={{ fontWeight: "bold", color: "var(--clr-primary)", fontSize: "var(--fs-lg)" }}>
            OC-{folio}
          </span>
        </div>
        <div className="lmd-card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)" }}>
            {/* Clave proveedor */}
            <div className="lmd-field">
              <label className="lmd-label">Clave Proveedor *</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  className="lmd-input"
                  value={prov.clave}
                  onChange={(e) => setProv((p) => ({ ...p, clave: e.target.value.toUpperCase() }))}
                  onBlur={lookupProveedor}
                  onKeyDown={(e) => e.key === "Enter" && lookupProveedor()}
                  placeholder="Clave..."
                />
                <button className="lmd-btn lmd-btn-secondary" onClick={lookupProveedor} title="Buscar proveedor">
                  <Search size={14} />
                </button>
              </div>
              {provMsg && <span style={{ color: "var(--clr-danger, #dc2626)", fontSize: "var(--fs-xs)" }}>{provMsg}</span>}
            </div>

            {/* Nombre */}
            <div className="lmd-field" style={{ gridColumn: "span 2" }}>
              <label className="lmd-label">Nombre</label>
              <input className="lmd-input" value={prov.nombre} readOnly style={{ background: "#f9fafb" }} />
            </div>

            {/* RFC */}
            <div className="lmd-field">
              <label className="lmd-label">RFC</label>
              <input className="lmd-input" value={prov.rfc} readOnly style={{ background: "#f9fafb" }} />
            </div>

            {/* Fecha */}
            <div className="lmd-field">
              <label className="lmd-label">Fecha *</label>
              <input
                className="lmd-input" type="date" value={form.fecha}
                onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
              />
            </div>

            {/* Moneda */}
            <div className="lmd-field">
              <label className="lmd-label">Moneda</label>
              <select
                className="lmd-select" value={form.moneda}
                onChange={(e) => setForm((f) => ({ ...f, moneda: e.target.value }))}
                style={{ width: "100%" }}
              >
                <option value="P">Pesos (MXN)</option>
                <option value="D">Dólares (USD)</option>
              </select>
            </div>

            {/* Surtida */}
            <div className="lmd-field" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox" checked={form.surtida}
                  onChange={(e) => setForm((f) => ({ ...f, surtida: e.target.checked }))}
                />
                <span className="lmd-label" style={{ margin: 0 }}>Surtida</span>
              </label>
            </div>
          </div>

          {/* Dirección (2ª fila) */}
          {prov.direccion && (
            <div style={{ marginTop: "var(--sp-3)", fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
              {[prov.direccion, prov.colonia, prov.ciudad, prov.estado, prov.cp].filter(Boolean).join(", ")}
              {prov.telefono && ` — Tel: ${prov.telefono}`}
            </div>
          )}
        </div>
      </div>

      {/* ── Artículos ── */}
      <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Artículos</h3>
          <button className="lmd-btn lmd-btn-secondary" onClick={addRow}
            style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Plus size={14} /> Agregar fila
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-sm)" }}>
            <thead>
              <tr style={{ background: "var(--clr-table-head-bg, #f3f4f6)", borderBottom: "2px solid var(--clr-border)" }}>
                {["Código", "Cantidad", "Unidad", "Descripción", "P. Unitario", "% Desc.", "Importe", ""].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: h === "Importe" || h === "P. Unitario" ? "right" : "left", fontWeight: "var(--fw-semibold)", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid var(--clr-border)" }}>
                  {/* Código */}
                  <td style={{ padding: "4px 6px", minWidth: 100 }}>
                    <CellInput
                      value={r.articulo}
                      onChange={(e) => onRowChange(idx, "articulo", e.target.value.toUpperCase())}
                      onBlur={() => lookupArticulo(idx)}
                      placeholder="Clave..."
                    />
                    {rowMsgs[idx] && (
                      <div style={{ color: "var(--clr-danger, #dc2626)", fontSize: "var(--fs-xs)", marginTop: 2 }}>
                        {rowMsgs[idx]}
                      </div>
                    )}
                  </td>
                  {/* Cantidad */}
                  <td style={{ padding: "4px 6px", minWidth: 80 }}>
                    <CellInput
                      type="number" value={r.cantidad} align="right"
                      onChange={(e) => onRowChange(idx, "cantidad", e.target.value)}
                    />
                  </td>
                  {/* Unidad */}
                  <td style={{ padding: "4px 6px", minWidth: 80 }}>
                    <CellInput
                      value={r.unidadMedida}
                      onChange={(e) => onRowChange(idx, "unidadMedida", e.target.value)}
                      placeholder="PZA"
                    />
                  </td>
                  {/* Descripción */}
                  <td style={{ padding: "4px 6px", minWidth: 200 }}>
                    <CellInput
                      value={r.descripcion}
                      onChange={(e) => onRowChange(idx, "descripcion", e.target.value)}
                      placeholder="Descripción..."
                    />
                  </td>
                  {/* Precio Unitario */}
                  <td style={{ padding: "4px 6px", minWidth: 110 }}>
                    <CellInput
                      type="number" value={r.precioUnitario} align="right"
                      onChange={(e) => onRowChange(idx, "precioUnitario", e.target.value)}
                    />
                  </td>
                  {/* % Descuento */}
                  <td style={{ padding: "4px 6px", minWidth: 80 }}>
                    <CellInput
                      type="number" value={r.porcDescuento} align="right"
                      onChange={(e) => onRowChange(idx, "porcDescuento", e.target.value)}
                    />
                  </td>
                  {/* Importe */}
                  <td style={{ padding: "4px 6px", minWidth: 110, textAlign: "right", fontWeight: "var(--fw-medium)" }}>
                    {fmt(r.importe)}
                  </td>
                  {/* Eliminar */}
                  <td style={{ padding: "4px 6px", textAlign: "center" }}>
                    {rows.length > 1 && (
                      <button
                        className="lmd-btn lmd-btn-ghost lmd-btn-sm"
                        style={{ color: "var(--clr-danger, #dc2626)" }}
                        onClick={() => rmRow(idx)}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div style={{
          display: "flex", gap: "var(--sp-8)", justifyContent: "flex-end",
          padding: "12px 16px", borderTop: "2px solid var(--clr-border)",
          flexWrap: "wrap",
        }}>
          <span style={{ color: "var(--clr-text-muted)", fontSize: "var(--fs-sm)" }}>
            Subtotal antes desc.: <b>{fmt(totales.subtAntes)}</b>
          </span>
          <span style={{ color: "var(--clr-text-muted)", fontSize: "var(--fs-sm)" }}>
            Descuento: <b>{fmt(totales.desc)}</b>
          </span>
          <span style={{ fontSize: "var(--fs-sm)" }}>
            Subtotal: <b>{fmt(totales.sub)}</b>
          </span>
          <span style={{ fontSize: "var(--fs-sm)" }}>
            IVA (16%): <b>{fmt(totales.iva)}</b>
          </span>
          <span style={{ fontSize: "var(--fs-md)", fontWeight: "var(--fw-bold)", color: "var(--clr-primary)" }}>
            Total: {fmt(totales.tot)}
          </span>
        </div>
      </div>

      {/* ── Notas y Observaciones ── */}
      <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Notas</h3>
        </div>
        <div className="lmd-card-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--sp-4)" }}>
            <div className="lmd-field">
              <label className="lmd-label">Notas Adicionales</label>
              <textarea
                className="lmd-input"
                rows={4}
                value={form.notasAdicionales}
                onChange={(e) => setForm((f) => ({ ...f, notasAdicionales: e.target.value }))}
                style={{ resize: "vertical" }}
              />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Observaciones</label>
              <textarea
                className="lmd-input"
                rows={4}
                maxLength={250}
                value={form.observaciones}
                onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
                style={{ resize: "vertical" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Botones ── */}
      <div style={{ display: "flex", gap: "var(--sp-3)", justifyContent: "flex-end", paddingBottom: "var(--sp-6)" }}>
        <button className="lmd-btn lmd-btn-secondary" onClick={() => navigate("/proveedores/ordenes-compra")}>
          Cancelar
        </button>
        {isEdit && (
          <button
            className="lmd-btn lmd-btn-secondary"
            onClick={() => navigate(`/proveedores/ordenes-compra/${id}/print`)}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Printer size={14} /> Imprimir
          </button>
        )}
        <button className="lmd-btn lmd-btn-primary" onClick={handleGuardar} disabled={saving}>
          {saving ? "Guardando…" : isEdit ? "Actualizar" : "Guardar Orden"}
        </button>
      </div>
    </>
  );
}
